import type { Field } from '@formily/core';
import { useField } from '@formily/react';
import { observer } from '@formily/reactive-react';
import { TreeSelect, type TreeSelectProps } from 'antd';
import { useState, useMemo, useEffect } from 'react';

import { searchJmesPath } from '../../../utils';
import { getFormContext } from '../../utils/formContext';
import { buildFiltersParam } from '../../utils/filterHelpers';
import useFetchModelInstancesCursorPagination from '../../hooks/api/useFetchModelInstancesCursorPagination';
import { transformToReferenceValue } from '../reference-select/utils';
import type { IReferenceValue } from '../reference-select/helper';
import type { IFormReferenceTreeSelectProps } from './helper';
import { buildReferenceTree, type ITreeOption } from './treeBuilder';

// Not lazy: the whole list is fetched in one call, so we need a large page size.
// Not model-specific — just "load everything" — so it stays a fallback here.
const DEFAULT_PAGE_SIZE = 100; // API max page size; async/lazy loading is out of scope

// Mirrors reference-select: the BE-enriched reference must be stripped to the
// clean shape before submit. Detect "still enriched" by markers the BE adds
// (never produced by transformToReferenceValue, so this can't loop).
const ENRICHED_REFERENCE_MARKERS = ['data', 'modelVersionId', 'updatedAt'];
const isEnrichedReference = (value: any): boolean =>
  !!value &&
  typeof value === 'object' &&
  ENRICHED_REFERENCE_MARKERS.some(key => key in value);
const shouldBuildInitialValue = (initialValue: any): boolean => {
  if (!initialValue) return false;
  if (Array.isArray(initialValue))
    return initialValue.some(isEnrichedReference);
  return isEnrichedReference(initialValue);
};

const toCodeArray = (value: any): string[] => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (value === undefined || value === null || value === '') return [];
  return [String(value)];
};

const FormReferenceTreeSelect = observer(() => {
  const field = useField<Field>();
  const componentProps = (field.componentProps ||
    {}) as IFormReferenceTreeSelectProps;
  const {
    modelIds,
    modelInstanceFilters,
    transformer,
    childKey,
    parentKey,
    selectableExpression,
    leavesOnly = false,
    numberToLoadOptions,
    treeDefaultExpandAll = false,
    placeholder,
    disabled: disabledProp,
    allowClear,
    mode,
    size,
    ...rest
  } = componentProps;

  const disabled = disabledProp || field.disabled;
  const isMultiple = mode === 'multiple' || mode === 'tags';
  const formContext = useMemo(() => getFormContext(field.form), [field.form]);

  // Originally-loaded items, so removals in multi-select can be flagged
  // `toBeDeleted` instead of dropped (BE ignores omitted array items).
  const [originalItems] = useState<IReferenceValue[]>(() => {
    const initial = field.value ?? field.initialValue;
    return Array.isArray(initial) ? (initial as IReferenceValue[]) : [];
  });

  // Strip the enriched initial value down to the clean reference shape.
  // Reactive to initialValue since reference data can arrive after mount.
  useEffect(() => {
    if (shouldBuildInitialValue(field.initialValue)) {
      const transformed = transformToReferenceValue(
        field.initialValue,
        transformer
      );
      field.setInitialValue(transformed);
      field.setValue(transformed);
    }
  }, [field.initialValue]);

  const { modelInstances, isLoadingModelInstances } =
    useFetchModelInstancesCursorPagination({
      params: {
        modelIds,
        filters: buildFiltersParam({
          staticFilters: [modelInstanceFilters],
          context: formContext,
          syntax: 'runtime',
        }),
        size: numberToLoadOptions || DEFAULT_PAGE_SIZE,
        page: 1,
      },
      options: {
        enabled: !!modelIds && modelIds.length > 0,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    });

  const rawData = modelInstances?.data;

  // Build the flat option list: transformer supplies value/label/modelId/version,
  // childKey/parentKey/selectableExpression are read from the raw instance.
  const options = useMemo<ITreeOption[]>(() => {
    if (!Array.isArray(rawData) || !transformer) return [];
    const itemTransformer = transformer.replace('[].', '');

    return rawData.map((item: any) => {
      const t = searchJmesPath(item, itemTransformer) || {};
      return {
        id: t.value ?? item.id,
        label: t.label ?? item.id,
        modelId: t.modelId ?? item.modelId,
        version: t.version ?? item.version,
        childCode: childKey ? String(searchJmesPath(item, childKey) ?? '') : '',
        parentCodes: parentKey
          ? toCodeArray(searchJmesPath(item, parentKey))
          : [],
        selectable: selectableExpression
          ? !!searchJmesPath(item, selectableExpression)
          : true,
        // Carry the full transformer output so configurator-added fields (e.g.
        // `termId: data.termId`) are available downstream via the value's `_meta`.
        meta: t,
      };
    });
  }, [rawData, transformer, childKey, parentKey, selectableExpression]);

  const treeData = useMemo(
    () => buildReferenceTree(options, leavesOnly),
    [options, leavesOnly]
  );

  const optionById = useMemo(
    () => new Map(options.map(o => [o.id, o])),
    [options]
  );

  // Expose options for reactions (parity with reference-select dataSource sync).
  useEffect(() => {
    if (options.length) field.setState({ dataSource: options });
  }, [options]);

  const selectValue = useMemo(() => {
    const value = field.value;
    if (!value) return undefined;
    if (Array.isArray(value)) {
      return value
        .filter((v: IReferenceValue) => !v.toBeDeleted)
        .map((v: IReferenceValue) => v.id);
    }
    if (typeof value === 'object' && 'id' in value) {
      return (value as IReferenceValue).id;
    }
    return undefined;
  }, [field.value]);

  const toReferenceValue = (id: string): IReferenceValue => {
    const option = optionById.get(id);
    const original = originalItems.find(o => o.id === id);
    return {
      id,
      modelId: option?.modelId ?? original?.modelId,
      version: option?.version ?? original?.version,
      _formDisplayValue: option?.label ?? original?._formDisplayValue ?? id,
      _meta: option?.meta ?? original?._meta,
    };
  };

  const handleChange = (value: string | string[] | undefined) => {
    if (isMultiple) {
      const selectedIds = Array.isArray(value) ? value : [];
      const referenceValues = selectedIds.map(toReferenceValue);

      originalItems.forEach(original => {
        if (!selectedIds.includes(original.id)) {
          referenceValues.push({ ...original, toBeDeleted: true });
        }
      });

      field.setValue(referenceValues);
      field.validate();
      return;
    }

    if (!value || Array.isArray(value)) {
      field.setValue(undefined);
      return;
    }

    field.setValue(toReferenceValue(value));
    field.validate();
  };

  return (
    <TreeSelect
      {...rest}
      value={selectValue}
      onChange={handleChange as TreeSelectProps['onChange']}
      treeData={treeData as TreeSelectProps['treeData']}
      treeNodeFilterProp="title"
      showSearch
      treeDefaultExpandAll={treeDefaultExpandAll}
      placeholder={placeholder || 'Select...'}
      disabled={disabled}
      allowClear={allowClear}
      size={size}
      loading={isLoadingModelInstances}
      style={{ width: '100%' }}
      {...(isMultiple
        ? {
            treeCheckable: true,
            showCheckedStrategy: TreeSelect.SHOW_ALL,
          }
        : {})}
    />
  );
});

export default FormReferenceTreeSelect;
